import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Star } from "lucide-react";
import { useCasting } from "@/context/CastingContext";
import { useLanguage } from "@/context/LanguageContext";

export const AgencyLogo = ({ className = "h-10 w-auto" }) => (
  <img src="/diamotims-logo.png" alt="Diamotims Agency" className={className} />
);

const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div className={`flex items-center border border-white/15 ${className}`} data-testid="lang-toggle">
      {["fr", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}`}
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
            lang === l ? "bg-ivory text-black" : "text-white/55 hover:text-gold"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { board } = useCasting();
  const { t } = useLanguage();
  const location = useLocation();

  const LINKS = [
    { to: "/models", label: t("nav.models"), testid: "nav-link-models" },
    { to: "/actors", label: t("nav.actors"), testid: "nav-link-actors" },
    { to: "/voice", label: t("nav.voice"), testid: "nav-link-voice" },
    { to: "/discover", label: t("nav.discover"), testid: "nav-link-discover" },
    { to: "/new-faces", label: t("nav.newFaces"), testid: "nav-link-new-faces" },
    { to: "/about", label: t("nav.about"), testid: "nav-link-about" },
    { to: "/contact", label: t("nav.contact"), testid: "nav-link-contact" },
  ];

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#0A0A0A]/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-[1800px] items-center justify-between px-4 py-4 sm:px-8 lg:px-16">
        <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-3">
          <AgencyLogo className="h-11 w-auto" />
        </Link>

        <nav className="hidden items-center gap-6 lg:flex xl:gap-7">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `nav-link ${isActive ? "text-white" : "text-white/55"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <span
            data-testid="nav-casting-count"
            className="flex items-center gap-2 border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-gold"
          >
            <Star className="h-3 w-3" /> {board.length}
          </span>
          <LangToggle />
          <Link
            to="/clients"
            data-testid="nav-link-clients"
            className="border border-gold/60 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:bg-gold hover:text-black"
          >
            {t("nav.workWithUs")}
          </Link>
        </nav>

        <div className="flex items-center gap-3 lg:hidden">
          <LangToggle />
          <button
            data-testid="nav-mobile-toggle"
            aria-label={t("nav.toggleMenu")}
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center border border-white/15 text-white"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <nav
          data-testid="nav-mobile-menu"
          className="border-t border-white/10 bg-[#0A0A0A]/95 backdrop-blur-xl lg:hidden"
        >
          <div className="flex max-h-[70vh] flex-col overflow-y-auto px-4 py-6 sm:px-8">
            {[
              { to: "/", label: t("nav.home"), testid: "nav-link-home" },
              ...LINKS,
              { to: "/clients", label: t("nav.workWithUs"), testid: "nav-link-clients" },
              { to: "/submissions", label: t("nav.submissions"), testid: "nav-link-submissions" },
            ].map((l, i) => (
              <NavLink
                key={`${l.to}-${i}`}
                to={l.to}
                data-testid={`${l.testid}-mobile`}
                className={({ isActive }) =>
                  `flex items-center justify-between border-b border-white/10 py-4 font-display text-2xl font-light uppercase tracking-tight ${
                    isActive ? "text-gold" : "text-white"
                  }`
                }
              >
                {l.label}
                <span className="font-mono text-[10px] tracking-[0.3em] text-white/40">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
};
